/**
 * Getting an ID or licence photo ready to send.
 *
 * The live site accepts request bodies up to 2 MB (nginx), and the bytes go as
 * base64, which is a third larger. So a photo is redrawn at a sensible size as
 * a JPEG first — a phone photo of a passport is often 4–8 MB and is perfectly
 * legible at 1600 pixels. A PDF cannot be redrawn, so it has to be small
 * already. Either way the platform still reads the real type from the bytes.
 */

/** The most the page will send, before base64. Under the 2 MB limit with room for the envelope. */
export const MAX_EVIDENCE_BYTES = 1_400_000;
const LONG_EDGE = 1600;

export interface PreparedEvidence {
  readonly filename: string;
  readonly bytes: Uint8Array;
}

export class EvidenceError extends Error {}

const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

export const EVIDENCE_ACCEPT = ACCEPTED.join(',');

export async function prepareEvidence(file: File): Promise<PreparedEvidence> {
  if (!ACCEPTED.includes(file.type)) {
    throw new EvidenceError('Choose a photo (JPEG, PNG or WebP) or a PDF.');
  }
  if (file.type !== 'application/pdf') {
    const redrawn = await redraw(file);
    if (redrawn !== undefined && redrawn.byteLength <= MAX_EVIDENCE_BYTES) {
      return { filename: `${baseName(file.name)}.jpg`, bytes: redrawn };
    }
  }
  if (file.size > MAX_EVIDENCE_BYTES) {
    throw new EvidenceError(
      file.type === 'application/pdf'
        ? 'That PDF is too large to send. Use a photo instead, or a PDF under 1.4 MB.'
        : 'That photo is too large to send. Try another photo.',
    );
  }
  return { filename: file.name, bytes: await readBytes(file) };
}

/** A Blob's bytes; FileReader where `arrayBuffer` is missing (older browsers, test DOMs). */
function readBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === 'function')
    return blob.arrayBuffer().then((buffer) => new Uint8Array(buffer));
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(new EvidenceError('That file could not be read. Try another.'));
    reader.readAsArrayBuffer(blob);
  });
}

function baseName(name: string): string {
  const dot = name.lastIndexOf('.');
  const stem = (dot > 0 ? name.slice(0, dot) : name).replace(/[^\w.-]+/g, '-');
  return stem === '' ? 'document' : stem.slice(0, 120);
}

/** Redraws a photo as a JPEG no larger than LONG_EDGE. Undefined where the browser cannot. */
async function redraw(file: File): Promise<Uint8Array | undefined> {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return undefined;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, LONG_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d');
    if (context === null) return undefined;
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    return blob === null ? undefined : await readBytes(blob);
  } catch {
    return undefined;
  }
}
